import { Dialog, Spinner } from 'construct-ui';
import moment from 'moment';
import paginationComponent from '../../components/pagination/pagination.component';
import SingleSelectComponent from '../../components/single-select/single-select.component';
import { formats } from '../../config/constants';
import SMECustomerSiteMapModel from '../../models/sme-customer-if-site-map/sme-customer-if-site-map.model';
import ViewBase from '../view-base.view';
import SMECustomerSiteMapAddEditDialog from './sme-customer-if-site-map.dialog';

/**
 * SME Customer I/F Site Map
 * 
 * A page that displays a list of SME Customer I/F Site Mapping and CRUD features
 * 
 * @author cmcnkhiep
 */

export default function SMECustomerSiteMapView() {
  ViewBase.call(this);

  const DESCENDING_TEXT = 'DESC';
  const ASCENDING_TEXT = 'ASC';
  const ADD_ACTION = 'ADD';
  const UPDATE_ACTION = 'UPDATE';
  const DELETE_ACTION = 'DELETE';
  const DEFAULT_MAP_TYPE = 'CHANNEL_MAP';
  const DEFAULT_PAGINATION = {
    pageIndex: 1,
    pageSize: 10,
    totalCount: 0
  };
  const DEFAULT_ITEM = {
    action: ADD_ACTION,
    smeEdiCustomerCode: "",
    mapType: DEFAULT_MAP_TYPE,
    mapKey: "",
    mapAttribute1: "",
    mapAttribute2: ""
  };

  let sMECustomerOptions = [];
  let tableData = [];
  let isLoadingTable = false;
  let selectedSortBy = '';
  let selectedOrderBy = '';
  let isUpdateDialogOpen = false;
  let isDeleteConfirmOpen = false;
  let selectedItem = structuredClone(DEFAULT_ITEM);

  const searchFields = {
    sMEEdiCustomerCode: '',
    mapType: DEFAULT_MAP_TYPE,
    samsungChannel: '',
    iFChannelAttr1: '',
    iFChannelAttr2: '',
    isExcelDownload: false
  }

  let currentRequestParams = structuredClone(searchFields);

  let pagination = structuredClone(DEFAULT_PAGINATION);

  const flipOrderText = (orderText) => {
    return orderText.toUpperCase() === DESCENDING_TEXT ? ASCENDING_TEXT : DESCENDING_TEXT;
  }

  const onSortClick = (orderBy) => {
    if (!tableData.length) {
      return
    }
    if (selectedOrderBy === orderBy) {
      selectedSortBy = flipOrderText(selectedSortBy);
    } else {
      selectedOrderBy = orderBy;
      selectedSortBy = DESCENDING_TEXT;
    }
    getTableData();
  };

  const getSortIcon = (orderBy) => {
    if (selectedOrderBy !== orderBy) {
      return null;
    }

    return selectedSortBy === DESCENDING_TEXT
      ? m('i.bi.bi-sort-down', { "style": "float:left" })
      : m('i.bi.bi-sort-up', { "style": "float:left" });
  };

  let onPageSizeChanged = (pageIndex, pageSize) => {
    pagination.pageIndex = pageIndex;
    pagination.pageSize = pageSize;
    getTableData();
  }

  let onPageChanged = (pageIndex) => {
    pagination.pageIndex = pageIndex;
    getTableData();
  }

  const requestParams = {
    get pageIndex() {
      return pagination.pageIndex;
    },

    get pageSize() {
      return pagination.pageSize;
    },

    get orderBy() {
      return selectedOrderBy;
    },

    get sortBy() {
      return selectedSortBy;
    },

    get sMEEdiCustomerCode() {
      return currentRequestParams.sMEEdiCustomerCode;
    },

    get samsungChannel() {
      return currentRequestParams.samsungChannel.trim();
    },

    get iFChannelAttr1() {
      return currentRequestParams.iFChannelAttr1.trim();
    },

    get iFChannelAttr2() {
      return currentRequestParams.iFChannelAttr2.trim();
    },

    get mapType() {
      return currentRequestParams.mapType;
    },

    get isExcelDownload() {
      return currentRequestParams.isExcelDownload;
    }
  }

  function resetSearchFields() {
    searchFields.sMEEdiCustomerCode = '';
    searchFields.samsungChannel = '';
    searchFields.iFChannelAttr1 = '';
    searchFields.iFChannelAttr2 = '';
    searchFields.isExcelDownload = false;
  }

  function updateSearchFields(attr, value) {
    if (Array.isArray(searchFields[attr])) {
      searchFields[attr].splice(0, Infinity, ...value);
    } else {
      searchFields[attr] = value;
    }
  }

  let getSMECustomerOptions = () => {
    this.showLoading();
    sMECustomerOptions.splice(0);

    return SMECustomerSiteMapModel.getSMECustomerList()
      .then((result) => {
        if (result?.length > 0) {
          result.map(i => {
            sMECustomerOptions.push(i)
          })
        }
      })
      .catch((error) => {
        sMECustomerOptions.splice(0);
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });
  }

  let getTableData = () => {
    isLoadingTable = true;
    currentRequestParams.isExcelDownload = false;
    SMECustomerSiteMapModel.searchSMECustomerSiteMap(requestParams)
      .then((result) => {
        if (!result) {
          tableData = [];
          return;
        }

        pagination.totalCount = result.total;
        tableData = result.data;
      })
      .catch((error) => {
        tableData = [];
        this.handleError(error);
      })
      .finally(() => {
        isLoadingTable = false;
      })
  }

  let search = (e) => {
    // Form validation
    if (!this.validateForm(e)) {
      return false;
    }

    pagination.pageIndex = DEFAULT_PAGINATION.pageIndex;
    currentRequestParams = structuredClone(searchFields)
    getTableData()

    return false;
  }

  let clearFilters = (e) => {
    e.preventDefault();
    let form = document.querySelector('.needs-validation');
    form.classList.remove('was-validated');
    resetSearchFields();
  }

  const handleAddClick = () => {
    selectedItem = structuredClone(DEFAULT_ITEM);
    isUpdateDialogOpen = true;
  }

  const handleUpdateClick = (item) => {
    selectedItem = structuredClone({
      action: UPDATE_ACTION,
      smeEdiCustomerCode: item.smeEdiCustomerCode,
      mapType: item.mapType,
      mapKey: item.samsungChannel,
      mapAttribute1: item.ifChannelAttr1,
      mapAttribute2: item.ifChannelAttr2
    });
    isUpdateDialogOpen = true;
  }

  const onDeleteConfirm = (item) => {
    selectedItem = structuredClone({
      action: DELETE_ACTION,
      smeEdiCustomerCode: item.smeEdiCustomerCode,
      mapType: item.mapType,
      mapKey: item.samsungChannel,
      mapAttribute1: item.ifChannelAttr1,
      mapAttribute2: item.ifChannelAttr2
    });
    isDeleteConfirmOpen = true;
  }

  const handleDelete = () => {
    this.showLoading();

    SMECustomerSiteMapModel.postSMECustomerSiteMapAction(selectedItem)
      .then((result) => {
        this.showMessage(
          this.getText('EM00000004', 'SUCCESS'),
          this.getText('LT00000475', 'Success'),
          () => {
            isDeleteConfirmOpen = false;
            getTableData();
          }
        );
      })
      .catch((error) => {
        this.handleError(error);
      })
      .finally(() => {
        this.hideLoading();
      });

  }

  const downloadSMECustomerSiteMapExcel = () => {
    currentRequestParams.isExcelDownload = true;
    SMECustomerSiteMapModel.downloadSMECustomerSiteMapExcel(requestParams)
  }

  return {
    oninit: (vnode) => {
      getSMECustomerOptions();
    },
    view: (vnode) => {
      return [
        // Title
        m('.d-flex.justify-content-end.mb-3',
          m("button.btn.btn-outline-primary.btn-add.mb-2",
            {
              onclick: handleAddClick,
            },
            m("i.bi.bi-plus-lg.me-0")
          )
        ),

        // Search section
        m('.d-flex.flex-column.align-items-end.gap-2.mb-3',
          m('#search-section.card.w-100.mb-0',
            m('.card-body',
              m('form.row.g-4.align-items-end.needs-validation[novalidate]',
                {
                  onsubmit: search
                },
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for=sMEEdiCustomerCode]', this.getText("LT00008046", "Customer")),
                  m(SingleSelectComponent, {
                    id: 'sMEEdiCustomerCode',
                    filterable: false,
                    keyOnly: true,
                    items: sMECustomerOptions,
                    selectedItem: searchFields.sMEEdiCustomerCode,
                    onSelect: (selectedItems) => {
                      updateSearchFields('sMEEdiCustomerCode', selectedItems);
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="samsungChannel"]', this.getText("LT00001012", "Samsung Channel")),
                  m('input#samsungChannel.form-control[name="samsungChannel"][maxLength="50"]', {
                    placeholder: this.getText("LT00001012", "Samsung Channel"),
                    value: searchFields.samsungChannel,
                    onchange: (e) => {
                      updateSearchFields('samsungChannel', e.target.value)
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="iFChannelAttr1"]', this.getText("LT00001008", "I/F Channel Attr 1")),
                  m('input#iFChannelAttr1.form-control[name="iFChannelAttr1"][maxLength="20"]', {
                    placeholder: this.getText("LT00001008", "I/F Channel Attr 1"),
                    value: searchFields.iFChannelAttr1,
                    onchange: (e) => {
                      updateSearchFields('iFChannelAttr1', e.target.value)
                    }
                  })
                ),
                m('.col-xl-3.col-lg-4.col-sm-6.position-relative',
                  m('label.form-label[for="iFChannelAttr2"]', this.getText("LT00001009", "I/F Channel Attr 2")),
                  m('input#iFChannelAttr2.form-control[name="iFChannelAttr2"][maxLength="20"]', {
                    placeholder: this.getText("LT00001009", "I/F Channel Attr 2"),
                    value: searchFields.iFChannelAttr2,
                    onchange: (e) => {
                      updateSearchFields('iFChannelAttr2', e.target.value)
                    }
                  })
                ),

                // Search & clear buttons
                m('.col-12.d-flex.justify-content-end.gap-2',
                  m('button.btn.btn-primary[type="submit"]', {
                  }, this.getText("LT00000001", "Search")),
                  m('button.btn.btn-white', {
                    onclick: clearFilters
                  }, this.getText("LT00000031", "Clear")),
                ),
              ),
            )
          ),
        ),
        // Search section - end

        // Table section
        m('.card',
          m('.card-body',
            m('.row.g-2 ',
              m('.col-12.d-flex.justify-content-end',
                m('button.btn.btn-primary',
                  {
                    disabled: tableData.length === 0,
                    onclick: downloadSMECustomerSiteMapExcel
                  },
                  m('i.bi.bi-file-earmark-arrow-down-fill'),
                  this.getText('LT00000018', 'Excel Download')
                )
              ),
              m('.col-12.p-0',
                m('.table-responsive',
                  m('table.table', [
                    m('thead.thead-bggrey',
                      m('tr.align-middle',
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('smeEdiCustomerCode'); } },
                          getSortIcon('smeEdiCustomerCode'),
                          this.getText("LT00008046", "Customer")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('mapType'); } },
                          getSortIcon('mapType'),
                          this.getText("LT00001011", "Map Type")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('samsungChannel'); } },
                          getSortIcon('samsungChannel'),
                          this.getText("LT00001012", "Samsung Channel")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('ifChannelAttr1'); } },
                          getSortIcon('ifChannelAttr1'),
                          this.getText("LT00001008", "I/F Channel Attr 1")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('ifChannelAttr2'); } },
                          getSortIcon('ifChannelAttr2'),
                          this.getText("LT00001009", "I/F Channel Attr 2")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('lastUpdatedDate'); } },
                          getSortIcon('lastUpdatedDate'),
                          this.getText("LT00000234", "Last Updated Date")
                        ),
                        m('th.cursor-pointer[scope="col"]',
                          { onclick: () => { onSortClick('lastUpdateUID'); } },
                          getSortIcon('lastUpdateUID'),
                          this.getText("LT00001010", "Last Updated User Id")
                        ),
                        m('th[scope="col"]',
                          this.getText("LT00000092", "Action")
                        )
                      )
                    ),
                    m('tbody',
                      m(Spinner, {
                        active: isLoadingTable,
                        background: true,
                        fill: true,
                        size: 'xl',
                      }),
                      (tableData.length == 0) &&
                      m('tr.align-middle',
                        m('td[colspan=22]', this.getText("EM00000141", "No records found."))
                      ),
                      tableData.length > 0 &&
                      tableData.map((col, index) => [
                        m('tr.align-middle',
                          m('td', col.smeEdiCustomerCode),
                          m('td', col.mapType),
                          m('td', col.samsungChannel),
                          m('td', col.ifChannelAttr1),
                          m('td', col.ifChannelAttr2),
                          m('td', moment(col.lastUpdatedDate).format(formats.LOCAL_TIME)),
                          m('td', col.lastUpdateUID),
                          m('td',
                            m('i.bi.bi-pencil-square.list-action-icon[title="Edit"]',
                              {
                                onclick: () => { handleUpdateClick(col); }
                              }),
                            m('i.bi.bi-trash-fill.list-action-icon[title="Delete"]',
                              {
                                onclick: () => { onDeleteConfirm(col); }
                              })
                          )
                        )
                      ])
                    )
                  ])
                ),
                tableData.length > 0 &&
                m(".px-3",
                  m(paginationComponent, {
                    currentPage: pagination.pageIndex,
                    pageSize: pagination.pageSize,
                    totalItems: pagination.totalCount,
                    onPageChanged: onPageChanged,
                    onPageSizeChanged: onPageSizeChanged
                  })
                )
              )
            )
          )
        ),
        // Table section - end

        // SME Customer I/F Site Mapping Dialog - start
        isUpdateDialogOpen &&
        m(SMECustomerSiteMapAddEditDialog,
          {
            updateItem: selectedItem,
            sMECustomerOptions: sMECustomerOptions,
            isOpen: isUpdateDialogOpen,
            onClose: (reload) => {
              if (reload === true) {
                getTableData();
              }
              isUpdateDialogOpen = false;
            },
          }
        ),

        // SME Customer I/F Site Mapping Dialog - end

        // Delete Confirm Dialog - start
        m(Dialog,
          {
            isOpen: isDeleteConfirmOpen,
            onClose: () => { isDeleteConfirmOpen = false },
            class: 'cui-dialog-lg',
            title: this.getText('LT00000043', 'Confirm'),
            content: m('container',
              this.getText('EM00000796', 'Are you sure you want to delete selected site mapping?')
            ),
            footer: m('.d-flex.gap-2.justify-content-end',
              m('button.btn.btn-white', { onclick: () => { isDeleteConfirmOpen = false } }, this.getText("LT00000024", "No")),
              m('button.btn.btn-primary', { onclick: handleDelete }, this.getText("LT00000023", "Yes"))
            )
          }
        ),

        // Delete Confirm Dialog - end

        this.getBaseViews()
      ]
    }
  }
}